import { NotificationManager } from '@sravni/react-design-system';
import { useBoolean } from '@sravni/react-utils';
import { useEffect } from 'react';
import { v7 as uuidv7 } from 'uuid';

import { useAppDispatch, useAppSelector } from 'shared/lib';
import { getAnalyticFromSummaries } from 'shared/lib/getAnalyticFromSummaries';
import { sendEventSubmitFullDealApplication } from 'shared/lib/sendGAEvents';
import { useUpdateStateBySelector } from 'shared/lib/useUpdateStateBySelector';
import type { TOffer } from 'shared/types/TRankingService';

import type { TMfoDataFormFields } from 'entities/creditForm';
import { MfoFullDealFormValidationSchema, selectGender } from 'entities/creditForm';
import { getFormattedValues } from 'entities/mfo/lib/getFormattedValues';
import { selectMfoData } from 'entities/mfo/model/mfoForm.selectors';
import { postDealsFastInit } from 'entities/mfo/model/postDealsFastInit.query';

import { MfoFullDealFormViewHoc } from './FormBody';
import { useInitSnilsData } from './lib/useInitSnilsData';
import { FormFieldsControls } from './MfoFullDealForm.config';
import { BUTTON_LABEL, MfoFullDealTexts, NOTIFICATION_ERROR } from './MfoFullDealForm.texts';

type Props = {
  offer: TOffer;
  onClose: () => void;
  sendReferralOffer: (offer: TOffer, other: any) => void;
};

export const MfoFullDealForm = ({ offer, onClose, sendReferralOffer }: Props) => {
  const [isLoading, setIsLoading] = useBoolean(false);

  useInitSnilsData();

  const [formData] = useUpdateStateBySelector(selectMfoData);

  const dispatch = useAppDispatch();
  const gender = useAppSelector(selectGender);

  useEffect(() => {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    });
  }, []);

  const onDataChanged = async (actualData: TMfoDataFormFields, type: string) => {
    if (type === 'fullFilled') {
      try {
        setIsLoading.on();
        const { amount, period, snils } = actualData;

        const isDaysPeriod = amount < 100_000;

        await dispatch(
          postDealsFastInit.endpoints.initDeal.initiate({
            summ: amount,
            period: `P${period}${isDaysPeriod ? 'D' : 'M'}`,
            snils,
            gender: gender?.toLowerCase() || '',
          }),
        );

        const uuid = uuidv7();

        await sendReferralOffer({ ...offer, productName: 'Онлайн на карту' }, { amount, period, uuid });

        if (offer?.buttonLink) {
          const {
            amount: formattedAmount,
            period: formattedPeriod,
            snils: formattedSnils,
          } = getFormattedValues(actualData);
          const { additionalValue } = getAnalyticFromSummaries(offer.summaries);

          sendEventSubmitFullDealApplication({
            fillType: 'manual',
            amount: formattedAmount,
            term: formattedPeriod,
            snils: formattedSnils,
            mfoName: offer?.bankName,
            rate: additionalValue,
          });
          onClose();
          window.location.href = offer?.buttonLink;
        }
      } catch {
        NotificationManager.show({
          type: NOTIFICATION_ERROR.type,
          title: NOTIFICATION_ERROR.title,
          message: NOTIFICATION_ERROR.message,
        });
      } finally {
        setIsLoading.off();
      }
    }
  };

  return (
    <MfoFullDealFormViewHoc
      defaultData={formData}
      onDataChanged={onDataChanged}
      validationSchema={MfoFullDealFormValidationSchema()}
      isLoading={isLoading}
      formLabels={MfoFullDealTexts}
      formFieldsControls={FormFieldsControls}
      submitButtonText={BUTTON_LABEL}
    />
  );
};
